import React, { useMemo } from 'react';
import {
  Heart, IndianRupee, Car, Plane, Briefcase, Smile,
  Cpu, Shield, Home, Umbrella, Building2, TrendingUp,
  Zap, Award, PiggyBank, MapPin,
} from 'lucide-react';

// One-time keyframe injection so we don't force Tailwind config edits.
// Two drift patterns + a subtle rotate — staggered so icons don't sync.
const KEYFRAMES = `
@keyframes floatSymbolA { 0%{transform:translate(0,0) rotate(0)} 50%{transform:translate(28px,-34px) rotate(6deg)} 100%{transform:translate(0,0) rotate(0)} }
@keyframes floatSymbolB { 0%{transform:translate(0,0) rotate(0)} 50%{transform:translate(-32px,26px) rotate(-8deg)} 100%{transform:translate(0,0) rotate(0)} }
@keyframes floatSymbolC { 0%{transform:translate(0,0) rotate(-3deg)} 50%{transform:translate(18px,32px) rotate(3deg)} 100%{transform:translate(0,0) rotate(-3deg)} }
`;

// 16 icons across the insurance world — order matters for stagger.
const ICONS = [
  Heart, IndianRupee, Car, Plane, Briefcase, Smile,
  Cpu, Shield, Home, Umbrella, Building2, TrendingUp,
  Zap, Award, PiggyBank, MapPin,
];

// Deterministic layout — 16 fixed anchor points across the viewport so the
// backdrop feels intentional (not random-looking). Each anchor gets an
// animation variant + delay + duration for the drift-parallax feel.
const ANCHORS = [
  { top: '6%',  left: '8%',  size: 96, delay: 0,   dur: 14, anim: 'A', tint: 'text-blue-800/10' },
  { top: '12%', left: '28%', size: 64, delay: 3,   dur: 16, anim: 'B', tint: 'text-emerald-700/10' },
  { top: '4%',  left: '48%', size: 80, delay: 6,   dur: 18, anim: 'C', tint: 'text-amber-600/10' },
  { top: '10%', left: '72%', size: 72, delay: 2,   dur: 15, anim: 'A', tint: 'text-purple-700/10' },
  { top: '2%',  left: '90%', size: 56, delay: 5,   dur: 17, anim: 'B', tint: 'text-slate-700/10' },
  { top: '32%', left: '4%',  size: 72, delay: 4,   dur: 19, anim: 'C', tint: 'text-red-600/10' },
  { top: '38%', left: '38%', size: 104,delay: 1,   dur: 20, anim: 'A', tint: 'text-indigo-700/10' },
  { top: '30%', left: '60%', size: 56, delay: 7,   dur: 15, anim: 'B', tint: 'text-teal-700/10' },
  { top: '44%', left: '82%', size: 88, delay: 3,   dur: 17, anim: 'C', tint: 'text-orange-700/10' },
  { top: '58%', left: '14%', size: 64, delay: 6,   dur: 16, anim: 'B', tint: 'text-emerald-700/10' },
  { top: '64%', left: '46%', size: 80, delay: 2,   dur: 18, anim: 'A', tint: 'text-blue-800/10' },
  { top: '60%', left: '68%', size: 72, delay: 5,   dur: 19, anim: 'C', tint: 'text-purple-700/10' },
  { top: '72%', left: '88%', size: 56, delay: 4,   dur: 15, anim: 'B', tint: 'text-amber-600/10' },
  { top: '82%', left: '6%',  size: 88, delay: 8,   dur: 20, anim: 'A', tint: 'text-indigo-700/10' },
  { top: '86%', left: '34%', size: 60, delay: 1,   dur: 16, anim: 'C', tint: 'text-red-600/10' },
  { top: '78%', left: '60%', size: 96, delay: 3,   dur: 21, anim: 'B', tint: 'text-teal-700/10' },
];

/**
 * Full-bleed decorative backdrop of insurance-themed icons that gently
 * drift with parallax. Absolutely positioned, non-interactive, keeps
 * content crisp above with a subtle radial vignette.
 *
 * Usage: place inside a `relative` container as the first child, then
 * wrap actual content in a `relative z-10` sibling.
 */
const FloatingSymbols = ({ opacity = 1, className = '' }) => {
  const items = useMemo(
    () => ANCHORS.map((a, i) => ({ ...a, Icon: ICONS[i % ICONS.length], id: `sym-${i}` })),
    []
  );
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: KEYFRAMES }} />
      <div
        className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
        style={{ opacity }}
        aria-hidden="true"
        data-testid="floating-symbols"
      >
        {/* Soft radial vignette so the icons feel embedded, not pasted. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.06), transparent 60%),' +
              'radial-gradient(ellipse at 80% 80%, rgba(139,92,246,0.06), transparent 60%)',
          }}
        />
        {items.map(({ id, Icon, top, left, size, delay, dur, anim, tint }) => (
          <Icon
            key={id}
            className={tint}
            style={{
              position: 'absolute',
              top,
              left,
              width: size,
              height: size,
              animation: `floatSymbol${anim} ${dur}s ease-in-out ${delay}s infinite`,
              willChange: 'transform',
              filter: 'blur(0.3px)',
            }}
          />
        ))}
      </div>
    </>
  );
};

export default FloatingSymbols;
